/**
 * This is a container for all route handlers. It can be simple and
 * dumb as is (passes route params down to RouteHandler),
 * but allows for an extra layer of control over all nested routes in needed.
 */

/**
 * Dependencies
 */
import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';
import {
  StructuredContentProvider,
  createClient,
  ClientContext,
} from '@nerdwallet/structured-content';
import { MarketplaceContextProvider } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import {
  useImpression,
  ImpressionContext,
} from '@nerdwallet/analytics/react/impression';
import { Tracking as nwTracking } from '@nerdwallet/nw-client-lib';

import appConfig from '@nerdwallet/app-config';
import { getNerdlayoutDynamicOptions } from '@nerdwallet/wp-api/utils/meta-data';
import GlobalMarkupOptions from '@nerdwallet/coin/GlobalMarkupOptions';
import ComparisonGridPanel from '@nerdwallet/side-by-side-compare';
import * as compareCreditCardsBarActions from '@nerdwallet/compare-credit-cards-bar/actions';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';

import { getSlug } from '~/server/config-fetcher';

import components from '../lib/structured-content/components';
import createProviders from '../lib/structured-content/providers';
import schema from '../lib/structured-content/schema';
import { CdsFiltersContextProvider } from '../contexts/cds-filters-context/cds-filters-context';
import { getQueryParams } from '../lib/query-param-helpers';
import { useSetGlobalModelExecutionIds } from '../lib/tracking';
import { Experiment, isExperimentAvailable } from '../lib/experiment-list';
import { useComparisonPanelProps } from '../components/comparison-cards/use-comparison-panel-props';

const CLIENT_PATH = 'res.locals.structuredContentClient';
const MODEL_EXECUTION_ID_QUERY = 'model_execution_id';

const Container = ({
  children,
  compareCards,
  flatOutline,
  location,
  experiments,
  saveToCompare,
  removeFromCompare,
  lpcVersion,
}) => {
  // Extract model execution id from query param
  const params = getQueryParams({ location });
  const modelExecutionIds = params?.[MODEL_EXECUTION_ID_QUERY] && [
    params?.[MODEL_EXECUTION_ID_QUERY],
  ];
  useSetGlobalModelExecutionIds(modelExecutionIds);

  const clientFromContext = useContext(ClientContext);
  const { serverRequest } = clientFromContext;

  const pageConfig = _.filter(
    flatOutline,
    (component) => component.type === 'Page'
  )[0];
  const includeInactiveOrNonMonetizingProducts = _.get(
    pageConfig,
    'props.includeInactiveOrNonMonetizingProducts',
    false
  );
  const includeCompareCreditsCard = _.get(
    pageConfig,
    'props.includeCompareCreditsCard',
    false
  );

  // Bedrock tracking
  const contentId = Number(pageConfig?.contentId);

  // There won't be a contentId when initially creating a new page, so fire a
  // page_impression instead.
  const impressionParams = contentId
    ? {
        eventType: 'content_impression',
        payload: {
          content_id: contentId,
          content_source: 'wp',
        },
      }
    : {
        eventType: 'page_impression',
        entityName: 'landing_page_builder',
      };

  const { ref, ...impression } = useImpression(impressionParams);
  const { pageViewId, pageName } = useAnalyticsPageView();

  // Once we use the same provider for both MarketPlaceProductCard and
  // Keto Product card, we'll disable credit card legacy id for LPC v2
  const shouldUseCClegacyIds = !(lpcVersion === '2');

  useEffect(() => {
    // Legacy DWH tracking
    nwTracking.logPageView(location.pathname);
  }, [location.pathname]);

  // The StructuredContent integration in nw-app-module will preload all the data in the tree
  // (see https://github.com/NerdWallet/nw-app-module/blob/v13.26.1/src/server/render/index.js#L456)
  // during SSR and repeatedly attempt to re-create the whole tree using renderToStaticMarkup
  // (https://github.com/NerdWallet/nwjs-apollo/blob/6b07e41ff5af9fde68f5898d1de944e8d176dae7/src/ssr/getDataFromTree.js#L39)
  // until all the data fetches resolve. To avoid creating a new client on each `renderToStaticMarkup` call, we
  // attach the client to the serverRequest on the first pass through, and afterwards use that. We use `useMemo` to
  // similarly avoid recreating the client on client-side renders, where no server request exists.
  const client = useMemo(() => {
    if (_.get(serverRequest, CLIENT_PATH)) {
      return _.get(serverRequest, CLIENT_PATH);
    }

    const newClient = createClient({
      schema,
      components,
      createProviders: createProviders({
        serverRequest,
        includeInactiveOrNonMonetizingProducts,
        useLegacyIds: shouldUseCClegacyIds,
      }),
      serverRequest,
    });
    _.set(serverRequest, CLIENT_PATH, newClient);

    return newClient;
  }, [
    serverRequest,
    includeInactiveOrNonMonetizingProducts,
    shouldUseCClegacyIds,
  ]);

  // we want to give the children location as a prop so that they'll rerender if location changes
  // even if they're connected to store, so that the rerender will propagate down the tree
  const childrenWithLocation = React.Children.map(children, (child) =>
    React.cloneElement(child, { location })
  );

  // Set meta tags
  let dynamicOpts = {};

  if (pageConfig) {
    /* eslint-disable camelcase */
    const { nw_seo, media_details } = pageConfig;
    // Because we're currently retrieving the landing page through WP directly
    // instead of query0, we have to create a post object in the same shape as
    // what query0 would return.
    const featuredMedia = {
      mediaDetails: {
        sizes: Object.values(media_details?.sizes || {}),
      },
    };
    const post = {
      id: contentId,
      link: `${appConfig.NERDWALLET_BASE_URL}${appConfig.BASE_URI}/${getSlug(
        location.pathname
      )}`,
      type: 'nw_landing',
      seo: nw_seo || {},
      featuredMedia,
    };
    /* eslint-enable camelcase */
    dynamicOpts = getNerdlayoutDynamicOptions({ post });
    if (pageConfig?.meta?.theme) {
      dynamicOpts = {
        ...dynamicOpts,
        theme: pageConfig?.meta?.theme,
      };
    }
  }
  // We pluck the initial state of the CdsProductTable out here so that we can
  // set it as the default in the ContextProvider and both the CdsFilter and CdsProductTable
  // component can access it on a first server-side render
  const initialCdsState = Object.values(flatOutline).filter(
    ({ type }) => type === 'CdsProductTable'
  )?.[0]?.dataDependency?.data?.filters;

  const IS_AA_TEST_ACTIVE =
    isExperimentAvailable(Experiment.AA_TEST.id, experiments) &&
    Experiment.AA_TEST.slugs.includes(getSlug(location.pathname));

  const comparisonGridPanelProps = useComparisonPanelProps(
    compareCards,
    saveToCompare,
    removeFromCompare,
    client.dataProviders.store,
    {
      active: includeCompareCreditsCard,
      emptyLabel: _.get(
        pageConfig,
        'props.compareCreditsCardEmptyLabel',
        'Add a card'
      ),
    }
  );

  const Component = (
    <StructuredContentProvider client={client}>
      <CdsFiltersContextProvider
        initialLengthOfTerm={initialCdsState?.length_of_term}
        initialMinDeposit={initialCdsState?.deposit_minimum}
        initialMinRatings={initialCdsState?.min_ratings}
      >
        <MarketplaceContextProvider>
          <ImpressionContext.Provider value={impression}>
            <div ref={ref} className="route-container">
              <GlobalMarkupOptions>
                {{
                  ...dynamicOpts,
                  segmentTrackingAttributes: {
                    driverPageName: pageName,
                    // eslint-disable-next-line no-underscore-dangle
                    deployableName: appConfig?._meta?.deployable,
                    // eslint-disable-next-line no-underscore-dangle
                    deployableVersion: appConfig?._meta?.version,
                    driverContentId: String(contentId),
                    driverContentSource: 'wp',
                    pageViewId,
                  },
                }}
              </GlobalMarkupOptions>
              {childrenWithLocation}
            </div>
            {includeCompareCreditsCard ? (
              <ComparisonGridPanel
                productType={PRODUCT_TYPES.CREDIT_CARDS}
                classes={comparisonGridPanelProps.classes}
                comparisonGridProps={
                  comparisonGridPanelProps.comparisonGridProps
                }
                comparisonGridSkittleProps={
                  comparisonGridPanelProps.comparisonGridSkittleProps
                }
              />
            ) : null}
          </ImpressionContext.Provider>
        </MarketplaceContextProvider>
      </CdsFiltersContextProvider>
    </StructuredContentProvider>
  );

  if (!IS_AA_TEST_ACTIVE) {
    return Component;
  }

  return (
    <ABContainer experimentID={Experiment.AA_TEST.id}>
      <Control>{Component}</Control>
      <Variant variantName={Experiment.AA_TEST.variant.AA_VARIANT}>
        {Component}
      </Variant>
    </ABContainer>
  );
};

Container.displayName = 'RouteContainer';

Container.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
  flatOutline: PropTypes.object,
  experiments: PropTypes.object,
  removeFromCompare: PropTypes.func,
  saveToCompare: PropTypes.func,
  compareCards: PropTypes.shape({
    cardsToCompare: PropTypes.object,
  }),
  lpcVersion: PropTypes.oneOfType([PropTypes.string]),
};

const mapDispatchToProps = {
  removeFromCompare: compareCreditCardsBarActions.removeFromCompare,
  saveToCompare: compareCreditCardsBarActions.saveToCompare,
};

export default compose(
  withRouter,
  connect(
    createStructuredSelector({
      flatOutline: _.property('builder.flatOutline'),
      experiments: _.property('experiments'),
      compareCards: _.property('compareCards'),
      lpcVersion: _.property('builder.version'),
    }),
    mapDispatchToProps
  )
)(Container);
